import { ErrorsOr, flatMapErrorsOrK } from "@me8eon/errors";
import { AxiosStatic } from "axios";
import { AsyncStore } from "@me8eon/async_storage";
import { Codec } from "@me8eon/codec";


export type AzureBlobConfig<ID, T> = {
    accountName: string,
    containerName: string,
    sasToken: string,
    blobtype: string
    version: string
    blobNameFn: (id: ID) => string
    codec: Codec<T>
    axios: AxiosStatic
}

export type AzureLoadBlobStorageConfig<ID, T> = AzureBlobConfig<ID, T> & {
    defaultValue: (id: ID) => Promise<ErrorsOr<T>>
}

export type AzureStoreBlobStorageConfig<ID, T> = AzureBlobConfig<ID, T>

export type AzureBlobStorageConfig<ID, T> = AzureLoadBlobStorageConfig<ID, T> & AzureStoreBlobStorageConfig<ID, T>


export function azureBlobUrl<ID, T>(azureConfig: AzureBlobConfig<ID, T>, id: ID): string {
    const { accountName, containerName, blobNameFn, sasToken } = azureConfig;
    const blobName = blobNameFn(id);
    return `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}?${sasToken}`;
}

export async function storeOneAzureBlob<ID, T>(
    { axios, blobtype, version }: AzureStoreBlobStorageConfig<ID, T>,
    url: string,
    data: string,
    context: string,
    contentType: string = "text/plain; charset=utf-8",
): Promise<ErrorsOr<void>> {
    try {
        const response = await axios.put(url, data, {
            headers: {
                "x-ms-blob-type": blobtype,
                "x-ms-version": version,
                "Content-Type": contentType,
            },
            validateStatus: () => true,
        });

        if (response.status >= 400) {
            console.error(`❌ ${context} upload failed:`, response.status, response.data);
            return {
                errors: [`Failed to store ${context}: ${response.status} ${JSON.stringify(response.data)}`],
            };
        }

        return { value: undefined };
    } catch (e: any) {
        console.error(`⚠️ Error storing ${context}:`, e);
        return {
            errors: [`Error storing ${context}: ${e.message}`],
            extras: e,
        };
    }
}

export function azureBlobStore<ID, T>(azureConfig: AzureStoreBlobStorageConfig<ID, T>) {
    const { codec } = azureConfig;
    return async (id: ID, value: T): Promise<ErrorsOr<void>> => {
        const blobUrl = azureBlobUrl(azureConfig, id);
        return flatMapErrorsOrK(codec.encode(value), s => storeOneAzureBlob(azureConfig, blobUrl, s, `storing blob contents for ${id}`));
    };
}

// Helper to load a single blob as text or JSON
export async function loadOneAzureBlob<ID, T>(
    { axios, version }: AzureLoadBlobStorageConfig<ID, T>,
    url: string,
    context: string,
    responseType: "text" | "json" = "text",
): Promise<ErrorsOr<string | any>> {
    try {
        const fullUrl = new URL(url);
        fullUrl.searchParams.set("nocache", Date.now().toString());
        const response = await axios.get(fullUrl.toString(), {
            responseType,
            headers: { "x-ms-version": version },
            validateStatus: () => true,
        });

        if (response.status === 404) {
            return { value: undefined };
        }

        if (response.status >= 400) {
            console.error(`❌ Failed to load ${context}:`, response.status, response.data);
            return {
                errors: [`Failed to load ${context}: ${response.status} ${JSON.stringify(response.data)}`],
            };
        }

        return { value: response.data };
    } catch (e: any) {
        console.error(`⚠️ Error loading ${context}:`, e);
        return {
            errors: [`Error loading ${context}: ${e.message}`],
            extras: e,
        };
    }
}

// Main Azure blob loader
export function azureBlobLoad<ID, T>(azureConfig: AzureLoadBlobStorageConfig<ID, T>) {
    const { defaultValue, codec } = azureConfig;
    return async (id: ID, undefinedIfNotIn?: boolean): Promise<ErrorsOr<T>> => {
        const blobUrl = azureBlobUrl(azureConfig, id);
        return flatMapErrorsOrK(await loadOneAzureBlob(azureConfig, blobUrl, `loading azure blob for ${id}`),
            async (raw: string | undefined) => {
                if (raw == undefined) return undefinedIfNotIn ? { value: undefined as T } : defaultValue(id);
                else {return codec.decode(raw);}
            });
    };
}

export const deleteAzureBlob = <ID, T>(cfg: AzureBlobConfig<ID, T>) => async (id: ID) => {
    const url = azureBlobUrl(cfg, id);
    try {
        const resp = await cfg.axios.delete(url, {
            headers: { "x-ms-version": cfg.version },
            validateStatus: () => true,
        });
        if (![200, 202, 204].includes(resp.status)) {
            return { errors: [`Failed to delete blob id=${String(id)}: ${resp.status}`] };
        }
        return { value: undefined };
    } catch (e: any) {
        return { errors: [`Failed to delete blob id=${String(id)}: ${e.message}`], extras: e };
    }
};

export function azureBlobStorage<ID, T>(azureConfig: AzureBlobStorageConfig<ID, T>): AsyncStore<ID, T> {
    return {
        store: azureBlobStore(azureConfig),
        get: azureBlobLoad(azureConfig),
        delete: deleteAzureBlob(azureConfig),
    };
}